import { useState, useContext, useReducer } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { gql, useMutation } from '@apollo/client';
import { DeleteForever, Info } from '@mui/icons-material';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useSelector } from 'react-redux';

import { IUser } from '../../../types/user';
import { Container, FlexBetween, FlexEnd, Flex } from '../../../commonStyles';
import { LeaseContext } from '../../../context/LeaseContext';
import Dropdown from '../../common/formControls/Dropdown';
import CustomDialog from '../../common/CustomDialog';
import { IDocuments } from '../../../types/docusign';
import { useAppDispatch } from '../../../app/hooks';
import { showErrorSnackbar } from '../../../features/snackbar/snackbarSlice';
import {
  interiorFeatures,
  listingBrokerageCommission,
  outdoorTypes,
  parkingSpaces,
  parkingTypes,
  washroomTypes,
} from '../../../utils/Constants';
import { IListingParking, IWashroom, ITotalRoom, IRoom } from '../../../types/lease';
import { parkingReducer, washroomReducer, roomReducer } from './listingReducer';
import { UnitContext } from '../../../context/UnitContext';
import { selectProject } from '../../../features/project/projectSlice';
import { IAddress, IExecutor, IMergeTemplates } from '../../../types/project';
import { IMerge } from '../../../types/merge';
import { showSuccessSnackbar } from '../../../features/snackbar/snackbarSlice';
import { convertAllDates, createPdf, numToCurrency } from '../../../utils/Functions';

export const handleCommission = (commission: string, type: string) => {
  if (type === 'words') {
    if (commission.includes('+')) {
      let split = commission.split('+');
      if (split.length === 2) {
        return commission;
      } else {
        return commission.split('+')[0].trim();
      }
    } else return commission;
  } else if (type === 'amount') {
    if (commission.includes('+')) {
      let split = commission.split('+');
      if (split.length === 2) {
        return Number(
          commission
            .split('+')
            [split.length - 1].trim()
            .replace(/[^0-9.-]+/g, '')
        ).toString();
      } else {
        return '';
      }
    } else return '';
  }
};

const Listing = (props: ChildProps) => {
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const { users } = props;
  const { unit, filteredDeal } = useContext(UnitContext);
  const { saveListing, deleteListing, lease, setLease, updateLease, updateListing, mergeTemplates } = useContext(LeaseContext);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<string>('');
  const [selectedMerge, setSelectedMerge] = useState<IMerge | null>(null);
  const [dateStart, setDateStart] = useState<Date | null>(
    lease && lease.listing ? new Date(lease.listing.dateStart) : new Date(new Date().setHours(0, 0, 0, 0))
  );
  const [dateEnd, setDateEnd] = useState<Date | null>(
    lease && lease.listing ? new Date(lease.listing.dateEnd) : new Date(new Date().setDate(new Date().getDate() + 60))
  );
  const [dateAvailable, setDateAvailable] = useState<Date | null>(
    lease && lease.listing && lease.listing.dateAvailable ? new Date(lease.listing.dateAvailable) : null
  );
  const [amount, setAmount] = useState<string>(lease && lease.listing ? lease.listing.amount : '');
  const [address, setAddress] = useState<string>(lease && lease.address ? lease.address : '');
  const [developer, setDeveloper] = useState<IUser | null>(lease && lease.listing ? lease.listing.developer : null);
  const [executor, setExecutor] = useState<IUser | null>(lease && lease.listing ? lease.listing.executor : null);
  const [listingWords, setListingWords] = useState<string>(
    lease && lease.listing ? handleCommission(lease.listing.listing, 'words')! : `One Month's Rent + HST`
  );
  const [listingAmount, setListingAmount] = useState<string>(
    lease && lease.listing ? handleCommission(lease.listing.listing, 'amount')! : ''
  );
  const [cooperatingWords, setCooperatingWords] = useState<string>(
    lease && lease.listing ? handleCommission(lease.listing.cooperating, 'words')! : `Half Month's Rent + HST`
  );
  const [cooperatingAmount, setCooperatingAmount] = useState<string>(
    lease && lease.listing ? handleCommission(lease.listing.cooperating, 'amount')! : ''
  );
  const [holdover, setHoldover] = useState<string>(lease && lease.listing ? lease.listing.holdover : '90');
  const [mls, setMls] = useState<string>(lease && lease.listing ? lease.listing.mls : '');
  const [reason, setReason] = useState<string>('');
  const [newAmount, setNewAmount] = useState(lease && lease.listing ? lease.listing.pendingAmount : '');
  const [newDate, setNewDate] = useState<Date | null>(
    lease && lease.listing && lease.listing.pendingExpiryDate ? new Date(lease.listing.pendingExpiryDate) : null
  );

  // MLS Data Form
  const [possessionRemarks, setPossessionRemarks] = useState<string>(
    lease && lease.listing ? lease.listing.possessionRemarks : 'Immediately'
  );
  const [parkingCheckbox, setParkingCheckbox] = useState<boolean>(false);
  const [parkingsState, parkingsDispatch] = useReducer(
    parkingReducer,
    lease && lease.listing && lease.listing.parking ? lease.listing.parking : []
  );
  const [sqftSource, setSqftSource] = useState<string>(lease && lease.listing ? lease.listing.sqftSource : "Builder's Floor Plan");
  const [balcony, setBalcony] = useState<string>(lease && lease.listing ? lease.listing.balcony : '');
  const [interiorFeature, setInteriorFeature] = useState<string[]>(lease && lease.listing ? lease.listing.interiorFeatures : []);
  const [washroomsState, washroomsDispatch] = useReducer(
    washroomReducer,
    lease && lease.listing && lease.listing.washrooms && lease.listing.washrooms.length
      ? lease.listing.washrooms
      : [
          {
            count: '1',
            pieces: '0',
            level: 'Flat',
          },
        ]
  );
  const [totalRoomsState, totalRoomsDispatch] = useReducer(
    washroomReducer,
    lease && lease.listing && lease.listing.totalRooms && lease.listing.totalRooms.length
      ? lease.listing.totalRooms
      : [
          {
            name: 'Total Number of Rooms',
            count: '',
            additional: '',
          },
          {
            name: 'Number of Bedrooms',
            count: '',
            additional: '',
          },
          {
            name: 'Number of Kitchens',
            count: '',
            additional: '',
          },
        ]
  );
  const [roomsState, roomsDispatch] = useReducer(
    roomReducer,
    lease && lease.listing && lease.listing.rooms && lease.listing.rooms.length ? lease.listing.rooms : []
  );

  const [createDocument, { loading: createDocumentLoading }] = useMutation(CREATEDOCUMENT, {
    onCompleted: (data) => {
      updateListing({
        variables: {
          _id: lease.listing._id,
          record: {
            pendingAmount: newAmount ? parseInt(newAmount, 10) : null,
            pendingExpiryDate: newDate ? new Date(newDate) : null,
          },
        },
      });
      setLease({
        ...lease,
        documents: [...lease.documents, data.documentCreateOne],
        listing: {
          ...lease.listing,
          pendingAmount: newAmount ? parseInt(newAmount, 10) : null,
          pendingExpiryDate: newDate ? new Date(newDate) : null,
        },
      });
      let documents = lease.documents;
      if (lease.documents.length) {
        documents = [...lease.documents.map((document: IDocuments) => document._id), data.documentCreateOne._id];
      } else {
        documents = [data.documentCreateOne._id];
      }
      updateLease({
        variables: {
          _id: lease._id,
          record: {
            documents,
          },
        },
      });
      setSelectedMerge(null);
      setNewAmount('');
      setNewDate(null);
      storeDispatch(showSuccessSnackbar(`Document has been created!`));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const handleDropdownInput = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    let selectedUser = users.find((user: IUser) => user.fullName === event.target.value);
    if (selectedUser) {
      if (event.target.name === 'developer') {
        setDeveloper(selectedUser);
      } else if (event.target.name === 'executor') {
        setExecutor(selectedUser);
      }
    } else if (event.target.value === 'None') {
      if (event.target.name === 'developer') {
        setDeveloper(null);
      } else if (event.target.name === 'executor') {
        setExecutor(null);
      }
    }
  };

  const handleReducer = async (value: string | number, index: number, type: string, dispatch: any) => {
    await dispatch({
      type: 'UPDATE',
      payload: {
        index: index,
        field: type,
        value: value,
      },
    });
  };

  // Options
  const tooltip = () => {
    return (
      <Flex sx={{ flexDirection: 'column' }}>
        {filteredDeal.options.map((option: any) => {
          return (
            <Flex sx={{ flexWrap: 'wrap', textAlign: 'left' }}>
              <Box style={{ textAlign: 'left', marginRight: '10px' }}>
                {option.name} - x{option.purchaseAmount}
              </Box>
            </Flex>
          );
        })}
      </Flex>
    );
  };

  const handleListingModal = (type: string) => {
    setDialogType(type);
    setDialogOpen(true);
  };

  const dialogBox = () => {
    let removeButton = 'Cancel';
    let successButton = 'Create';
    let title = 'Listing Amendment';
    if (dialogType === 'delete') {
      title = 'Delete Listing';
      successButton = 'Delete';
    }
    if (dialogType === 'cancel') {
      title = 'Cancellation Amendment';
    }
    return (
      <CustomDialog
        handleClose={() => setDialogOpen(false)}
        handleCloseRemove={() => setDialogOpen(false)}
        handleCloseSuccess={handleCloseSuccess}
        open={dialogOpen}
        removeButton={removeButton}
        successButton={successButton}
        dialogContent={dialogContent()}
        dialogTitle={title}
      />
    );
  };

  const handleCloseSuccess = async () => {
    if (dialogType === 'delete') {
      if (lease.documents.find((document: IDocuments) => document.type === 'Listing')) {
        return storeDispatch(showErrorSnackbar('There is currently a Listing Envelope. Please delete the envelope to proceed.'));
      }
      deleteListing(lease);
    }
    if (dialogType === 'cancel' || dialogType === 'amend') {
      let purchasers: any[] = [];
      let options: any[] = [];
      let selectedMerge = null;
      if (dialogType === 'cancel') {
        selectedMerge = mergeTemplates.find((mergeTemplate: IMergeTemplates) =>
          mergeTemplate.name.toLowerCase().includes('cancellation of listing agreement')
        );
      }

      if (dialogType === 'amend') {
        if (lease.listing && newAmount && newDate) {
          selectedMerge = mergeTemplates.find((mergeTemplate: IMergeTemplates) =>
            mergeTemplate.name.toLowerCase().includes('amendment to listing agreement - price and date')
          );
        } else if (lease.listing && newAmount) {
          selectedMerge = mergeTemplates.find((mergeTemplate: IMergeTemplates) =>
            mergeTemplate.name.toLowerCase().includes('amendment to listing agreement - price')
          );
        } else if (lease.listing && newDate) {
          selectedMerge = mergeTemplates.find((mergeTemplate: IMergeTemplates) =>
            mergeTemplate.name.toLowerCase().includes('amendment to listing agreement - date')
          );
        }
      }

      if (!selectedMerge) {
        return storeDispatch(showErrorSnackbar(`No Merge Template Found`));
      }

      if (selectedMerge?.type === 'Management') {
        purchasers = lease.landlords;
        options = lease.management.options;
      }
      if (selectedMerge?.type === 'Listing') {
        purchasers = lease.landlords;
      }
      if (selectedMerge?.name === 'Tenancy Agreement') {
        purchasers = lease.landlords;
      }
      let pdf = await createPdf(
        selectedMerge,
        project,
        unit,
        purchasers,
        [],
        [],
        [],
        options,
        null,
        null,
        null,
        null,
        null,
        null,
        false,
        [],
        [],
        false,
        {
          ...lease,
          listing: {
            ...lease.listing,
            pendingAmount: newAmount ? newAmount : lease.listing.pendingAmount,
            pendingExpiryDate: newDate ? newDate : lease.listing.pendingExpiryDate,
            reason: reason
          },
        }
      );

      let sign = selectedMerge?.signFields.map((sign: any) => {
        return {
          index: sign.index,
          key: sign.key,
          pageNumber: sign.pageNumber,
          x: sign.x,
          y: sign.y,
        };
      });

      let mergeName = selectedMerge?.name;

      const args = {
        status: 'Prepared',
        name: mergeName,
        project: project._id,
        deal: filteredDeal._id,
        unit: unit._id,
        signFields: sign,
        // signers,
        isAPS: false,
        type: selectedMerge?.type,
      };

      setDialogOpen(false);
      createDocument({ variables: { record: args, file: pdf } });
    }
  };

  const dialogContent = () => {
    switch (dialogType) {
      case 'amend':
        return (
          <Box sx={{ my: 2 }}>
            <TextField
              title={'New Listing Amount'}
              sx={{ mb: 2 }}
              name={'newAmount'}
              fullWidth
              type="number"
              value={newAmount}
              label={'New Listing Amount'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewAmount(e.target.value)}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={'New Listing Expiry Date'}
                value={newDate}
                onChange={(newValue) => {
                  setNewDate(newValue);
                }}
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>
          </Box>
        );
      case 'cancel':
        return (
          <Box sx={{ my: 2 }}>
            <TextField
              title={'Reason'}
              name={'reason'}
              fullWidth
              value={reason}
              label={'Reason'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
            />
          </Box>
        );
      case 'delete':
        return <Box sx={{ my: 2 }}>Are you sure you would like to delete the Listing Profile?</Box>;
      default:
        return;
    }
  };

  return (
    <Box>
      {dialogBox()}
      {lease && lease.listing ? (
        <FlexEnd sx={{ my: 2 }}>
          <Button sx={{ mr: 2 }} variant="contained" color="primary" onClick={() => handleListingModal('cancel')}>
            Cancellation Amendment
          </Button>
          <Button sx={{ mr: 2 }} variant="contained" color="primary" onClick={() => handleListingModal('amend')}>
            Listing Amendment
          </Button>
          <Button variant="contained" color="error" onClick={() => handleListingModal('delete')}>
            Delete Listing
          </Button>
        </FlexEnd>
      ) : null}
      {lease.listing && (lease.listing.pendingAmount || lease.listing.pendingExpiryDate) ? (
        <Container>
          <Typography sx={{ mb: 2 }} variant={'h5'}>
            Pending Amendment
          </Typography>
          <Box>
            Pending Listing Amount: {lease.listing.pendingAmount ? `${numToCurrency.format(lease.listing.pendingAmount)}` : 'No Change'}
          </Box>
          <Box>
            Pending Expiry Date Change:{' '}
            {lease.listing.pendingExpiryDate ? `${convertAllDates(new Date(lease.listing.pendingExpiryDate), 'PP')}` : 'No Change'}
          </Box>
        </Container>
      ) : null}
      <form
        onSubmit={(e: any) =>
          saveListing(
            e,
            address,
            dateStart,
            dateEnd,
            dateAvailable,
            amount,
            executor,
            developer,
            listingWords,
            listingAmount,
            cooperatingWords,
            cooperatingAmount,
            holdover,
            mls,
            possessionRemarks,
            parkingsState,
            sqftSource,
            balcony,
            interiorFeature,
            washroomsState,
            totalRoomsState,
            roomsState
          )
        }
      >
        <Container sx={{ mt: 2 }}>
          <Typography variant={'h5'}>Miscellaneous</Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl required fullWidth>
                <InputLabel id="id-question-label">Address</InputLabel>
                <Select
                  value={address}
                  labelId="id-status-label"
                  id="id-status"
                  label="Address"
                  name="status"
                  required
                  onChange={(e) => {
                    setAddress(e.target.value);
                  }}
                >
                  {project.addresses.map((address: IAddress) => {
                    return (
                      <MenuItem value={`${address.streetAddress}, ${address.city}, ${address.province}, ${address.postalCode}`}>
                        {address.streetAddress}, {address.city}, {address.province}, {address.postalCode}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid spacing={2} container>
            <Grid item xs={12} sm={6} md={4} sx={{ mt: 2 }}>
              <TextField
                title={'Lease Amount'}
                name={'amount'}
                label="Lease Amount"
                type="number"
                onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                fullWidth
                onChange={(e: any) => setAmount(e.target.value)}
                value={amount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                required={true}
                placeholder={'Lease Amount'}
              />
            </Grid>
          </Grid>
          <Grid spacing={2} container sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={'Listing Start Date (YYYY/MM/DD)'}
                  value={dateStart}
                  onChange={(newValue) => {
                    setDateStart(newValue);
                  }}
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={'Listing End Date (YYYY/MM/DD)'}
                  value={dateEnd}
                  onChange={(newValue) => {
                    setDateEnd(newValue);
                  }}
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={'Listing Available Date (YYYY/MM/DD)'}
                  value={dateAvailable}
                  onChange={(newValue) => {
                    setDateAvailable(newValue);
                  }}
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
          <Grid spacing={2} container sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Dropdown
                title={`Listing Brokerage Commission Month's Rent`}
                menuList={listingBrokerageCommission}
                name={'listingWords'}
                handleSelect={(e: any) => setListingWords(e.target.value)}
                value={listingWords}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                title={`Listing Brokerage Additional Commission`}
                name={`listingAmount`}
                label="Listing Brokerage Additional Commission"
                fullWidth
                type="number"
                onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                onChange={(e: any) => setListingAmount(e.target.value)}
                value={listingAmount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                placeholder={`Listing Brokerage Additional Commission`}
              />
            </Grid>
          </Grid>
          <Grid spacing={2} container sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Dropdown
                title={`Cooperating Brokerage Commission Month's Rent`}
                menuList={listingBrokerageCommission}
                name={'cooperatingWords'}
                handleSelect={(e: any) => setCooperatingWords(e.target.value)}
                value={cooperatingWords}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                title={`Cooperating Brokerage Additional Commission`}
                name={`cooperatingAmount`}
                label="Cooperating Brokerage Additional Commission"
                fullWidth
                type="number"
                onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                onChange={(e: any) => setCooperatingAmount(e.target.value)}
                value={cooperatingAmount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                placeholder={`Cooperating Brokerage Additional Commission`}
              />
            </Grid>
          </Grid>
          <Grid spacing={2} container>
            <Grid item xs={12} sm={6} md={4} sx={{ mt: 2 }}>
              <TextField
                title={'Holdover Days'}
                name={'holdover'}
                label="Holdover"
                onChange={(e: any) => setHoldover(e.target.value)}
                value={holdover}
                fullWidth
                required={true}
                placeholder={'Holdover'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ mt: 2 }}>
              <TextField
                title={'MLS#'}
                name={'mls'}
                label="MLS#"
                onChange={(e: any) => setMls(e.target.value)}
                value={mls}
                fullWidth
                placeholder={'MLS#'}
              />
            </Grid>
          </Grid>
          <Grid spacing={2} container>
            <Grid item xs={12} sm={6} md={4} sx={{ mt: 2 }}>
              <Dropdown
                required
                title={'RDS Sales Rep'}
                menuList={users.map((user: IUser) => user.fullName)}
                name={'executor'}
                handleSelect={(e: any) => handleDropdownInput(e)}
                value={executor ? executor.fullName : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ mt: 2 }}>
              <Dropdown
                title={'Developer Signs Listing Agreement'}
                includeEmpty={true}
                required={project._id === '61f9934d3ace1c59fcb6c5de' ? true : false}
                menuList={project.executors.map((user: IExecutor) => user.name)}
                name={'developer'}
                handleSelect={(e: any) => handleDropdownInput(e)}
                value={developer ? developer.fullName : ''}
              />
            </Grid>
          </Grid>
        </Container>
        <Container sx={{ mt: 2 }}>
          <FlexBetween>
            <Typography variant={'h5'}>MLS Data Form</Typography>
            <Box>
              <a href={unit.getUrl} target="_blank" rel="noreferrer">
                <Button color="primary" variant="contained">
                  View Floor Plan
                </Button>
              </a>
            </Box>
          </FlexBetween>
          <Grid spacing={2} container sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                title={'Possession Remarks'}
                name={'possessionRemarks'}
                label="Possession Remarks"
                onChange={(e: any) => setPossessionRemarks(e.target.value)}
                value={possessionRemarks}
                inputProps={{
                  maxLength: 50,
                }}
                fullWidth
                placeholder={'Possession Remarks'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                title={'SQFT Source'}
                name={'sqftSource'}
                label="SQFT Source"
                onChange={(e: any) => setSqftSource(e.target.value)}
                value={sqftSource}
                inputProps={{
                  maxLength: 50,
                }}
                fullWidth
                placeholder={'Sqft Source'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Dropdown
                title={`Balcony`}
                menuList={outdoorTypes}
                name={'balcony'}
                handleSelect={(e: any) => setBalcony(e.target.value)}
                value={balcony}
                required
              />
            </Grid>
            <Grid item xs={12} sx={{ my: 3 }}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Flex>
                <Typography variant={'h2'}>
                  <strong>Parking</strong>
                </Typography>
                <Tooltip title={tooltip()}>
                  <Info sx={{ alignSelf: 'center', ml: 1 }} />
                </Tooltip>
              </Flex>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={parkingCheckbox}
                    onChange={async (e: any) => {
                      if (parkingCheckbox) {
                        parkingsDispatch({
                          type: 'DELETEALL',
                        });
                      } else {
                        parkingsDispatch({
                          type: 'ADD',
                        });
                      }
                      setParkingCheckbox(!parkingCheckbox);
                    }}
                  />
                }
                label={'Add Parking Spots'}
              />
            </Grid>
            <Grid item xs={12}>
              {parkingCheckbox || parkingsState.length ? (
                <Grid container spacing={2}>
                  {parkingsState.map((parking: IListingParking, index: number) => {
                    return (
                      <>
                        <Grid item xs={12} sm={6} md={3}>
                          <Dropdown
                            title={`Parking Space`}
                            menuList={parkingSpaces}
                            name={'parkingSpaces'}
                            handleSelect={(e: any) => handleReducer(e.target.value, index, 'space', parkingsDispatch)}
                            value={parking.space}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Dropdown
                            title={`Parking Type`}
                            menuList={parkingTypes}
                            name={'parkingTypes'}
                            handleSelect={(e: any) => handleReducer(e.target.value, index, 'type', parkingsDispatch)}
                            value={parking.type}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Dropdown
                            title={`Count`}
                            menuList={['1', '2', '3', '4', '5']}
                            name={'count'}
                            handleSelect={(e: any) => handleReducer(parseInt(e.target.value, 10), index, 'count', parkingsDispatch)}
                            value={parking.count ? parking.count.toString() : ''}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <DeleteForever
                            color="error"
                            sx={{ cursor: 'pointer' }}
                            onClick={() =>
                              parkingsDispatch({
                                type: 'DELETE',
                                payload: {
                                  index: index,
                                },
                              })
                            }
                          />
                        </Grid>
                      </>
                    );
                  })}
                  <Grid item xs={12}>
                    <FlexEnd sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          parkingsDispatch({
                            type: 'ADD',
                          });
                        }}
                      >
                        Add Another Parking Spot
                      </Button>
                    </FlexEnd>
                  </Grid>
                </Grid>
              ) : null}
            </Grid>
            <Grid item xs={12} sx={{ my: 3 }}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant={'h2'}>
                    <strong>Total Rooms</strong>
                  </Typography>
                </Grid>
                {totalRoomsState.map((totalRoom: ITotalRoom, index: number) => {
                  return (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          title={'Name'}
                          name={'name'}
                          label="Name"
                          disabled
                          value={totalRoom.name}
                          inputProps={{
                            maxLength: 50,
                          }}
                          fullWidth
                          placeholder={'name'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Dropdown
                          title={`Count`}
                          menuList={['0', '1', '2', '3', '4', '5', '6', '7', '8']}
                          name={'count'}
                          handleSelect={(e: any) => handleReducer(e.target.value, index, 'count', totalRoomsDispatch)}
                          value={totalRoom.count}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Dropdown
                          title={`Additional`}
                          menuList={['0', '1', '2', '3', '4', '5']}
                          name={'additional'}
                          handleSelect={(e: any) => handleReducer(e.target.value, index, 'additional', totalRoomsDispatch)}
                          value={totalRoom.additional}
                          required
                        />
                      </Grid>
                    </>
                  );
                })}
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ my: 3 }}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant={'h2'}>
                    <strong>Washrooms</strong>
                  </Typography>
                </Grid>
                {washroomsState.map((washroom: IWashroom, index: number) => {
                  return (
                    <>
                      <Grid item xs={12} sm={6} md={3}>
                        <Dropdown
                          title={`# Washrooms`}
                          menuList={['0', '1', '2', '3', '4', '5']}
                          name={'washrooms'}
                          handleSelect={(e: any) => handleReducer(e.target.value, index, 'count', washroomsDispatch)}
                          value={washroom.count ? washroom.count : ''}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Dropdown
                          title={`# Pieces`}
                          menuList={['0', '1', '2', '3', '4', '5']}
                          name={'pieces'}
                          handleSelect={(e: any) => handleReducer(e.target.value, index, 'pieces', washroomsDispatch)}
                          value={washroom.pieces}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Dropdown
                          title={`Level`}
                          menuList={washroomTypes}
                          name={'level'}
                          handleSelect={(e: any) => handleReducer(e.target.value, index, 'level', washroomsDispatch)}
                          value={washroom.level}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <DeleteForever
                          color="error"
                          sx={{ cursor: 'pointer' }}
                          onClick={() =>
                            washroomsDispatch({
                              type: 'DELETE',
                              payload: {
                                index: index,
                              },
                            })
                          }
                        />
                      </Grid>
                    </>
                  );
                })}
                <Grid item xs={12}>
                  <FlexEnd sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        washroomsDispatch({
                          type: 'ADD',
                        });
                      }}
                    >
                      Add Washroom
                    </Button>
                  </FlexEnd>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                multiple
                id="interior-features"
                options={interiorFeatures}
                freeSolo={false}
                getOptionLabel={(option: any) => option}
                isOptionEqualToValue={(option, value) => option === value}
                value={interiorFeature}
                onChange={(e, value) => setInteriorFeature(value)}
                renderInput={(params) => <TextField {...params} variant="outlined" label="Interior Features" />}
              />
            </Grid>
            <Grid item xs={12} sx={{ my: 3 }}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant={'h2'}>
                    <strong>Rooms</strong>
                  </Typography>
                </Grid>
                {roomsState.map((room: IRoom, index: number) => {
                  return (
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={2}>
                          <Select
                            sx={{ width: '100%' }}
                            title={`Level`}
                            name={'level'}
                            onChange={(e: any) => handleReducer(e.target.value, index, 'level', roomsDispatch)}
                            value={room.level ? room.level : ''}
                            required
                          >
                            {washroomTypes.map((type: string) => {
                              return <MenuItem value={type}>{type}</MenuItem>;
                            })}
                          </Select>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                          <TextField
                            title={'Room Name'}
                            name={'name'}
                            label="Room Name"
                            onChange={(e: any) => handleReducer(e.target.value, index, 'name', roomsDispatch)}
                            value={room.name}
                            fullWidth
                            placeholder={'Room Name'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                          <TextField
                            title={'Length'}
                            name={'length'}
                            label="Length"
                            onChange={(e: any) => handleReducer(e.target.value, index, 'length', roomsDispatch)}
                            value={room.length}
                            type="number"
                            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                            inputProps={{
                              maxLength: 50,
                            }}
                            fullWidth
                            placeholder={'Length'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                          <TextField
                            title={'Width'}
                            name={'width'}
                            label="Width"
                            onChange={(e: any) => handleReducer(e.target.value, index, 'width', roomsDispatch)}
                            value={room.width}
                            type="number"
                            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                            inputProps={{
                              maxLength: 50,
                            }}
                            fullWidth
                            placeholder={'Length'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                          <TextField
                            title={'Height'}
                            name={'height'}
                            label="Height"
                            onChange={(e: any) => handleReducer(e.target.value, index, 'height', roomsDispatch)}
                            value={room.height}
                            type="number"
                            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                            inputProps={{
                              maxLength: 50,
                            }}
                            fullWidth
                            placeholder={'Height'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                          <TextField
                            title={'Description 1'}
                            name={'description1'}
                            label="Description 1"
                            onChange={(e: any) => handleReducer(e.target.value, index, 'description1', roomsDispatch)}
                            value={room.description1}
                            inputProps={{
                              maxLength: 50,
                            }}
                            fullWidth
                            placeholder={'Description 1'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                          <TextField
                            title={'Description 2'}
                            name={'description2'}
                            label="Description 2"
                            onChange={(e: any) => handleReducer(e.target.value, index, 'description2', roomsDispatch)}
                            value={room.description2}
                            inputProps={{
                              maxLength: 50,
                            }}
                            fullWidth
                            placeholder={'Description 2'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                          <TextField
                            title={'Description 3'}
                            name={'description3'}
                            label="Description 3"
                            onChange={(e: any) => handleReducer(e.target.value, index, 'description3', roomsDispatch)}
                            value={room.description3}
                            inputProps={{
                              maxLength: 50,
                            }}
                            fullWidth
                            placeholder={'Description 3'}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                          <DeleteForever
                            color="error"
                            sx={{ cursor: 'pointer' }}
                            onClick={() =>
                              roomsDispatch({
                                type: 'DELETE',
                                payload: {
                                  index: index,
                                },
                              })
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
                <Grid item xs={12}>
                  <FlexEnd sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        roomsDispatch({
                          type: 'ADD',
                        });
                      }}
                    >
                      Add Room
                    </Button>
                  </FlexEnd>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
        <FlexEnd sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" color="success">
            Save Listing Agreement
          </Button>
        </FlexEnd>
      </form>
    </Box>
  );
};

interface ChildProps {
  users: IUser[];
}

const CREATEDOCUMENT = gql`
  mutation documentCreateOne($record: CreateOneDocumentInput, $file: Upload!) {
    documentCreateOne(record: $record, file: $file) {
      _id
      name
      project {
        _id
      }
      type
      getUrl
      status
      isAPS
      dsEnvelopeId
    }
  }
`;

export default Listing;
